import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getMessageSenders,
  getMessageTemplates,
  logPersonalSend,
  previewBulkSend,
  sendMessage,
} from "../../services/messaging";
import type {
  BulkSendPreview,
  BulkSendPreviewRecipient,
  MessagingContactSummary,
  MessageSender,
  MessagingTemplate,
} from "../../types/messaging";
import { RecipientPicker } from "./RecipientPicker";
import { contactKey } from "./contactKey";
import { ModalCloseButton } from "../../components/ModalCloseButton";
import "./ComposeMessageModal.css";

interface ComposeMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Pre-selected recipients, e.g. opening a compose from a thread. Empty for the Inbox's
   * "New message" button, where the coach picks from scratch.
   */
  initialRecipients?: MessagingContactSummary[];
  /**
   * Whether the coach can change the recipient list from inside the composer.
   *
   * True for the Inbox, where picking from the roster is the whole point. False when the caller
   * already chose who this goes to — Trusted Contacts, say — because the picker searches
   * `/api/messaging/recipients`, which is the roster and holds none of them: a directory contact
   * removed from the list could never be added back.
   */
  showRecipientPicker?: boolean;
  /**
   * Something the caller needs the coach to read *here* rather than on the page behind — which
   * contacts were dropped from the selection and why. The composer is a fixed overlay, so a
   * notice left on the page underneath is only seen after it is closed.
   */
  notice?: string;
  onSent?: () => void;
}

const STATUS_LABELS: Record<BulkSendPreviewRecipient["status"], string> = {
  Sendable: "Will send",
  OptedOut: "Opted out",
  MissingMergeData: "Missing data",
  UnknownMergeField: "Template error",
  NoPhone: "No phone",
};

/**
 * Composes one message and walks a filtered list with it.
 *
 * Two things are load-bearing here and should not be "simplified" away:
 *
 * 1. The preview is fetched from the server before anything sends, and its
 *    counts are shown verbatim. Opted-out recipients appear in the list with a
 *    reason rather than being filtered out, so the numbers never look
 *    mysteriously smaller than the selection.
 * 2. Sending is a loop of single-recipient calls, never one call with an array.
 *    A multi-recipient composer creates a group thread in which every recipient
 *    sees every other recipient's number, and it cannot carry per-recipient
 *    merge fields.
 */
export const ComposeMessageModal = ({
  isOpen,
  onClose,
  initialRecipients,
  showRecipientPicker = true,
  notice,
  onSent,
}: ComposeMessageModalProps) => {
  // Insertion-ordered: the send queue walks this in the order the coach picked, so the progress
  // list matches what they were looking at.
  const [selected, setSelected] = useState<Map<string, MessagingContactSummary>>(
    () => new Map((initialRecipients ?? []).map((c) => [contactKey(c), c]))
  );

  const recipients = useMemo(
    () =>
      [...selected.values()].map((c) => ({
        contactId: c.contactId,
        contactType: c.contactType,
      })),
    [selected]
  );

  const [senders, setSenders] = useState<MessageSender[]>([]);
  const [templates, setTemplates] = useState<MessagingTemplate[]>([]);
  const [selectedSenderKey, setSelectedSenderKey] = useState<string>("");
  const [templateId, setTemplateId] = useState<string>("");
  const [body, setBody] = useState("");

  const [preview, setPreview] = useState<BulkSendPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sendingIndex, setSendingIndex] = useState<number | null>(null);
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());
  const [failures, setFailures] = useState<Record<string, string>>({});

  const selectedSender = useMemo(
    () => senders.find((s) => senderKey(s) === selectedSenderKey) ?? null,
    [senders, selectedSenderKey]
  );

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const load = async () => {
      try {
        const [loadedSenders, loadedTemplates] = await Promise.all([
          getMessageSenders(),
          getMessageTemplates(),
        ]);
        if (cancelled) return;

        setSenders(loadedSenders);
        setTemplates(loadedTemplates);
        // Default to an org number, never the personal phone. The senders endpoint lists
        // "My phone" first, so taking [0] would quietly make every message go out from the
        // coach's own number — replies would land on their handset instead of the shared
        // inbox, and nobody would notice until a recruit replied and no one saw it.
        setSelectedSenderKey((current) => {
          if (current) return current;
          const orgNumber = loadedSenders.find((s) => s.phoneNumberId);
          const fallback = orgNumber ?? loadedSenders[0];
          return fallback ? senderKey(fallback) : "";
        });
      } catch {
        if (!cancelled) setError("Failed to load senders.");
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Changing the message or the sender invalidates the preview — the counts are
  // specific to both, and a stale preview would misreport who gets suppressed.
  useEffect(() => {
    setPreview(null);
    setSentIds(new Set());
    setFailures({});
  }, [templateId, body, selectedSenderKey]);

  const handlePreview = useCallback(async () => {
    if (!templateId && !body.trim()) {
      setError("Pick a template or write a message first.");
      return;
    }

    setPreviewing(true);
    setError(null);
    try {
      const result = await previewBulkSend({
        templateId: templateId || undefined,
        body: templateId ? undefined : body,
        recipients,
        phoneNumberId: selectedSender?.phoneNumberId ?? null,
      });
      setPreview(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to build the preview."
      );
    } finally {
      setPreviewing(false);
    }
  }, [templateId, body, recipients, selectedSender]);

  const sendable = useMemo(
    () => preview?.recipients.filter((r) => r.status === "Sendable") ?? [],
    [preview]
  );

  const isPersonalSender = Boolean(selectedSender && !selectedSender.phoneNumberId);

  /**
   * Org-number sends only. The personal-phone queue is driven one tap at a time
   * by `handlePersonalSend` — looping `sms:` navigations here would fire only
   * the last one and take the page with it.
   */
  const handleSendAll = async () => {
    const phoneNumberId = selectedSender?.phoneNumberId;
    if (!preview || !phoneNumberId) return;

    setError(null);
    const nextFailures: Record<string, string> = {};
    const nextSent = new Set(sentIds);

    for (let i = 0; i < sendable.length; i++) {
      const recipient = sendable[i];
      const key = contactKey(recipient);
      if (nextSent.has(key)) continue;

      setSendingIndex(i);
      try {
        const result = await sendMessage({
          recipient: {
            contactId: recipient.contactId,
            contactType: recipient.contactType,
          },
          templateId: templateId || undefined,
          body: templateId ? undefined : body,
          phoneNumberId,
        });
        if (!result.sent) throw new Error(result.error ?? "Send failed.");

        nextSent.add(key);
        setSentIds(new Set(nextSent));
      } catch (err) {
        // One bad recipient must not abandon the rest of the queue.
        nextFailures[key] =
          err instanceof Error ? err.message : "Send failed.";
        setFailures({ ...nextFailures });
      }
    }

    setSendingIndex(null);
    onSent?.();
  };

  /**
   * One recipient, one composer, one tap. Records the attempt before handing off
   * because opening the composer may navigate away from this page — logging
   * afterwards would lose the record on the recipients that actually got sent.
   */
  const handlePersonalSend = async (recipient: BulkSendPreviewRecipient) => {
    setError(null);
    try {
      await logPersonalSend({
        recipient: {
          contactId: recipient.contactId,
          contactType: recipient.contactType,
        },
        body: recipient.resolvedBody ?? "",
        composerReportedSent: false,
      });
      setSentIds((current) => new Set(current).add(contactKey(recipient)));
      openNativeComposer(recipient);
      onSent?.();
    } catch (err) {
      setFailures((current) => ({
        ...current,
        [contactKey(recipient)]:
          err instanceof Error ? err.message : "Could not log the send.",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="compose-modal-overlay" role="dialog" aria-modal="true">
      <div className="compose-modal">
        <div className="compose-modal__header">
          <h2 className="compose-modal__title">New message</h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="compose-modal__body">
          {error && <div className="compose-modal__error">{error}</div>}
          {notice && (
            <div className="compose-modal__notice" role="status">
              {notice}
            </div>
          )}

          {showRecipientPicker ? (
            <RecipientPicker selected={selected} onChange={setSelected} />
          ) : (
            <div className="compose-modal__field">
              <span className="compose-modal__recipients-label" id="compose-fixed-recipients">
                To
              </span>
              <ul
                className="compose-modal__recipient-chips"
                aria-labelledby="compose-fixed-recipients"
              >
                {[...selected.values()].map((contact) => (
                  <li key={contactKey(contact)} className="compose-modal__recipient-chip">
                    {contact.displayName}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="compose-modal__field">
            <label htmlFor="compose-from">From</label>
            <select
              id="compose-from"
              value={selectedSenderKey}
              onChange={(e) => setSelectedSenderKey(e.target.value)}
            >
              {senders.map((sender) => (
                <option
                  key={senderKey(sender)}
                  value={senderKey(sender)}
                  disabled={!sender.isAvailable}
                >
                  {sender.label}
                  {sender.unavailableReason
                    ? ` — ${sender.unavailableReason}`
                    : ""}
                </option>
              ))}
            </select>
            {selectedSender && !selectedSender.supportsReplies && (
              <p className="compose-modal__note">
                Replies go to your own Messages app. Nobody else on staff will
                see them — use an organization number for conversations the
                whole staff should have.
              </p>
            )}
          </div>

          <div className="compose-modal__field">
            <label htmlFor="bulk-send-template">Template</label>
            <select
              id="bulk-send-template"
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              <option value="">Write a one-off message</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          {!templateId && (
            <div className="compose-modal__field">
              <label htmlFor="bulk-send-body">Message</label>
              <textarea
                id="bulk-send-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                maxLength={1600}
                placeholder="Hi {{firstName}}, we'd love to see you at camp."
              />
            </div>
          )}

          <button
            type="button"
            className="compose-modal__preview-btn"
            onClick={handlePreview}
            disabled={previewing || recipients.length === 0}
          >
            {previewing
              ? "Checking..."
              : recipients.length === 0
                ? "Pick someone to message"
                : `Preview ${recipients.length} ${recipients.length === 1 ? "recipient" : "recipients"}`}
          </button>

          {preview && (
            <>
              <div className="compose-modal__counts">
                <strong>{preview.selectedCount}</strong> selected ·{" "}
                <strong>{preview.optedOutCount}</strong> opted out ·{" "}
                <strong>{preview.blockedCount}</strong> blocked ·{" "}
                <strong>{preview.sendableCount}</strong> will send
              </div>

              {preview.exceedsPersonalQueueGuidance && (
                <div className="compose-modal__warning">
                  Each send from your own phone opens the composer and waits for
                  a tap. A queue this long takes a while to finish — consider
                  sending from an organization number instead.
                </div>
              )}

              <ul className="compose-modal__recipients">
                {preview.recipients.map((recipient) => {
                  // Prospect and Guardian share an id, so the id alone would mark a player sent
                  // when only their parent was.
                  const key = contactKey(recipient);
                  return (
                  <li
                    key={key}
                    className={`compose-modal__recipient compose-modal__recipient--${recipient.status.toLowerCase()}`}
                  >
                    <div className="compose-modal__recipient-head">
                      <span className="compose-modal__recipient-name">
                        {recipient.displayName}
                      </span>
                      <span className="compose-modal__recipient-status">
                        {sentIds.has(key)
                          ? "Sent"
                          : STATUS_LABELS[recipient.status]}
                      </span>
                    </div>

                    {recipient.resolvedBody && (
                      <p className="compose-modal__recipient-body">
                        {recipient.resolvedBody}
                      </p>
                    )}

                    {recipient.missingFields.length > 0 && (
                      <p className="compose-modal__recipient-missing">
                        Missing: {recipient.missingFields.join(", ")}
                      </p>
                    )}

                    {failures[key] && (
                      <p className="compose-modal__recipient-missing">
                        {failures[key]}
                      </p>
                    )}

                    {isPersonalSender &&
                      recipient.status === "Sendable" &&
                      !sentIds.has(key) && (
                        <button
                          type="button"
                          className="compose-modal__recipient-send"
                          onClick={() => handlePersonalSend(recipient)}
                        >
                          Open composer
                        </button>
                      )}
                  </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <div className="compose-modal__footer">
          {preview && isPersonalSender && (
            <span className="compose-modal__queue-progress">
              {sentIds.size} of {sendable.length} sent — tap each recipient in
              turn
            </span>
          )}
          <button type="button" onClick={onClose}>
            Close
          </button>
          {!isPersonalSender && (
            <button
              type="button"
              className="compose-modal__send-btn"
              onClick={handleSendAll}
              disabled={
                !preview || sendable.length === 0 || sendingIndex !== null
              }
            >
              {sendingIndex !== null
                ? `Sending ${sendingIndex + 1} of ${sendable.length}…`
                : `Send to ${sendable.length}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/** Stable option value — the personal sender has no number id to key on. */
const senderKey = (sender: MessageSender): string =>
  sender.phoneNumberId ?? `personal:${sender.channel}`;

/**
 * Hands one recipient's resolved body to the OS composer. One recipient per
 * composer, always — an `sms:` link with several addresses becomes a group
 * thread that leaks each recipient's number to the others.
 */
const openNativeComposer = (recipient: BulkSendPreviewRecipient) => {
  if (!recipient.phoneNumber) return;
  const body = encodeURIComponent(recipient.resolvedBody ?? "");
  window.location.href = `sms:${recipient.phoneNumber}?&body=${body}`;
};
